export interface Lesson {
  id: string;
  title: string;
  standards: string[];
  vocabulary: { term: string; definition: string }[];
  conceptExplanation: string;
  examples: {
    problem: string;
    steps: string[];
  }[];
  teacherTip: string;
  practiceQuestions: {
    question: string;
    options: string[];
    correctAnswer: number;
    explanation: string;
  }[];
}

export const algebraLessons: Lesson[] = [
  {
    id: 'seeing-structure-expressions',
    unit: 2,
    unitName: 'Algebra — Expressions & Equations',
    title: 'Seeing Structure in Expressions',
    standards: [
      'CCSS.MATH.CONTENT.HSA.SSE.A.1',
      'CCSS.MATH.CONTENT.HSA.SSE.A.2',
      'CCSS.MATH.CONTENT.HSA.SSE.B.3',
      'NY-9.A.SSE.1',
      'NY-9.A.SSE.2',
      'OH.MA.A1.SSE.1',
      'NCTM: Algebra - Expressions'
    ],
    vocabulary: [
      { term: 'Term', definition: 'A single number, variable, or the product of numbers and variables (e.g., 3x², -5y, 7)' },
      { term: 'Coefficient', definition: 'The numerical factor in a term (in 3x², the coefficient is 3)' },
      { term: 'Factor', definition: 'An expression that divides another expression evenly; expressions multiplied together' },
      { term: 'Equivalent Expression', definition: 'Different expressions that have the same value for all variable values' }
    ],
    conceptExplanation: `Understanding the structure of algebraic expressions helps us manipulate and simplify them effectively.

**Key Concepts:**

1. **Identifying Parts of Expressions:**
   - Terms are separated by + or - signs
   - Coefficients are the numbers multiplied by variables
   - Factors are expressions multiplied together
   - Example: In 3x² + 5x - 2, there are three terms: 3x², 5x, and -2

2. **Factoring Techniques:**
   - Greatest Common Factor (GCF): Factor out the largest common factor
   - Difference of Squares: a² - b² = (a + b)(a - b)
   - Perfect Square Trinomials: a² + 2ab + b² = (a + b)²
   - Factoring Trinomials: ax² + bx + c

3. **Completing the Square:**
   - Rewrite x² + bx + c in the form (x + p)² + q
   - Take half of the b coefficient, square it, and add/subtract
   - Useful for solving quadratics and graphing parabolas`,
    examples: [
      {
        problem: 'Factor completely: 2x³ + 8x² - 10x',
        steps: [
          'Step 1: Identify the GCF of all terms. The GCF is 2x.',
          'Step 2: Factor out 2x: 2x(x² + 4x - 5)',
          'Step 3: Factor the trinomial x² + 4x - 5. Find two numbers that multiply to -5 and add to 4: those are 5 and -1.',
          'Step 4: Write the factored form: 2x(x + 5)(x - 1)',
          'Final Answer: 2x(x + 5)(x - 1)'
        ]
      },
      {
        problem: 'Rewrite x² + 6x + 2 by completing the square',
        steps: [
          'Step 1: Take half of the coefficient of x: 6 ÷ 2 = 3',
          'Step 2: Square this value: 3² = 9',
          'Step 3: Add and subtract 9: x² + 6x + 9 - 9 + 2',
          'Step 4: Group the perfect square trinomial: (x² + 6x + 9) - 7',
          'Step 5: Factor the perfect square: (x + 3)² - 7',
          'Final Answer: (x + 3)² - 7'
        ]
      },
      {
        problem: 'Factor using difference of squares: 4x² - 25',
        steps: [
          'Step 1: Recognize this as a² - b² where a² = 4x² and b² = 25',
          'Step 2: Find a and b: a = 2x and b = 5',
          'Step 3: Apply the formula a² - b² = (a + b)(a - b)',
          'Final Answer: (2x + 5)(2x - 5)'
        ]
      }
    ],
    teacherTip: 'Always look for a GCF first before trying other factoring methods. Recognizing patterns like difference of squares and perfect square trinomials will save you time!',
    practiceQuestions: [
      {
        question: 'How many terms are in the expression 5x³ - 2x² + 7x - 9?',
        options: ['2 terms', '3 terms', '4 terms', '5 terms'],
        correctAnswer: 2,
        explanation: 'Terms are separated by + or - signs. This expression has four terms: 5x³, -2x², 7x, and -9.'
      },
      {
        question: 'What is the coefficient of x² in the expression 3x³ - 7x² + 2x - 1?',
        options: ['3', '-7', '2', '-1'],
        correctAnswer: 1,
        explanation: 'The coefficient is the number multiplied by the variable. For x², the coefficient is -7.'
      },
      {
        question: 'Factor: 3x + 12',
        options: ['3(x + 4)', 'x(3 + 12)', '3x + 12', '(3 + x)(12)'],
        correctAnswer: 0,
        explanation: 'The GCF of 3x and 12 is 3. Factor out 3: 3(x + 4).'
      },
      {
        question: 'Factor: x² - 16',
        options: ['(x - 4)(x - 4)', '(x + 4)(x + 4)', '(x - 16)(x + 1)', '(x + 4)(x - 4)'],
        correctAnswer: 3,
        explanation: 'This is a difference of squares: x² - 4² = (x + 4)(x - 4).'
      },
      {
        question: 'Factor: x² + 10x + 25',
        options: ['(x + 5)(x + 5)', '(x + 10)(x + 25)', '(x - 5)(x - 5)', '(x + 25)(x + 1)'],
        correctAnswer: 0,
        explanation: 'This is a perfect square trinomial: x² + 10x + 25 = (x + 5)².'
      },
      {
        question: 'Factor: 2x² + 10x',
        options: ['2(x² + 5x)', 'x(2x + 10)', '2x(x + 5)', '(2x + 5)(x)'],
        correctAnswer: 2,
        explanation: 'The GCF is 2x. Factor out 2x: 2x(x + 5).'
      },
      {
        question: 'Factor: x² + 7x + 12',
        options: ['(x + 3)(x + 4)', '(x + 2)(x + 6)', '(x + 1)(x + 12)', '(x + 7)(x + 12)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to 12 and add to 7: 3 and 4. So x² + 7x + 12 = (x + 3)(x + 4).'
      },
      {
        question: 'Factor: x² - 5x + 6',
        options: ['(x - 2)(x - 3)', '(x + 2)(x + 3)', '(x - 1)(x - 6)', '(x - 5)(x - 6)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to 6 and add to -5: -2 and -3. So x² - 5x + 6 = (x - 2)(x - 3).'
      },
      {
        question: 'Factor: 4x² - 9',
        options: ['(2x - 3)(2x - 3)', '(4x - 9)(x + 1)', '(2x + 3)(2x - 3)', '(x + 2)(x - 9)'],
        correctAnswer: 2,
        explanation: 'This is a difference of squares: (2x)² - 3² = (2x + 3)(2x - 3).'
      },
      {
        question: 'Complete the square for x² + 8x + 5. What value should be added and subtracted?',
        options: ['4', '8', '16', '64'],
        correctAnswer: 2,
        explanation: 'Take half of 8 (which is 4) and square it: 4² = 16. Add and subtract 16.'
      },
      {
        question: 'Rewrite x² + 8x + 5 by completing the square.',
        options: ['(x + 4)² - 11', '(x + 8)² - 59', '(x + 4)² + 5', '(x + 2)² + 1'],
        correctAnswer: 0,
        explanation: 'x² + 8x + 16 - 16 + 5 = (x + 4)² - 11.'
      },
      {
        question: 'Factor: x² - 2x - 15',
        options: ['(x - 5)(x + 3)', '(x + 5)(x - 3)', '(x - 15)(x + 1)', '(x - 3)(x + 5)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to -15 and add to -2: -5 and 3. So x² - 2x - 15 = (x - 5)(x + 3).'
      },
      {
        question: 'Factor: 3x² + 12x + 12',
        options: ['3(x + 2)(x + 2)', '(3x + 6)(x + 2)', '3(x + 4)(x + 1)', '(x + 2)(3x + 6)'],
        correctAnswer: 0,
        explanation: 'First factor out GCF of 3: 3(x² + 4x + 4). Then recognize perfect square: 3(x + 2)².'
      },
      {
        question: 'Which expression is equivalent to (x + 3)² - 4?',
        options: ['x² + 6x + 5', 'x² + 6x + 13', 'x² + 3x + 5', 'x² + 9x - 4'],
        correctAnswer: 0,
        explanation: 'Expand (x + 3)² = x² + 6x + 9, then subtract 4: x² + 6x + 9 - 4 = x² + 6x + 5.'
      },
      {
        question: 'Factor: x² + 6x + 9',
        options: ['(x + 3)(x + 3)', '(x + 9)(x + 1)', '(x + 6)(x + 9)', '(x - 3)(x - 3)'],
        correctAnswer: 0,
        explanation: 'This is a perfect square trinomial: x² + 6x + 9 = (x + 3)².'
      },
      {
        question: 'Factor: 5x² - 20',
        options: ['5(x² - 4)', '5(x + 2)(x - 2)', '(5x + 10)(x - 2)', '5(x - 4)(x + 1)'],
        correctAnswer: 1,
        explanation: 'Factor out GCF of 5: 5(x² - 4). Then use difference of squares: 5(x + 2)(x - 2).'
      },
      {
        question: 'What is the GCF of 6x³ + 9x² - 3x?',
        options: ['3', 'x', '3x', '6x'],
        correctAnswer: 2,
        explanation: 'The GCF of 6, 9, and 3 is 3. All terms have at least one x. So GCF = 3x.'
      },
      {
        question: 'Factor: x² - 9x + 20',
        options: ['(x - 4)(x - 5)', '(x + 4)(x + 5)', '(x - 10)(x + 2)', '(x - 2)(x - 10)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to 20 and add to -9: -4 and -5. So x² - 9x + 20 = (x - 4)(x - 5).'
      },
      {
        question: 'Rewrite x² - 4x + 1 by completing the square.',
        options: ['(x - 2)² - 3', '(x - 4)² + 1', '(x - 2)² + 1', '(x - 1)² - 4'],
        correctAnswer: 0,
        explanation: 'Half of -4 is -2, squared is 4. So x² - 4x + 4 - 4 + 1 = (x - 2)² - 3.'
      },
      {
        question: 'Factor: 9x² - 1',
        options: ['(3x - 1)(3x - 1)', '(9x - 1)(x + 1)', '(3x + 1)(3x - 1)', '(x + 3)(x - 3)'],
        correctAnswer: 2,
        explanation: 'This is a difference of squares: (3x)² - 1² = (3x + 1)(3x - 1).'
      },
      {
        question: 'Factor: x² + 11x + 30',
        options: ['(x + 5)(x + 6)', '(x + 10)(x + 3)', '(x + 15)(x + 2)', '(x + 11)(x + 30)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to 30 and add to 11: 5 and 6. So x² + 11x + 30 = (x + 5)(x + 6).'
      },
      {
        question: 'Factor: 2x² - 8',
        options: ['2(x² - 4)', '2(x + 2)(x - 2)', '(2x - 4)(x + 2)', '2(x - 4)(x + 1)'],
        correctAnswer: 1,
        explanation: 'Factor out GCF of 2: 2(x² - 4). Then use difference of squares: 2(x + 2)(x - 2).'
      },
      {
        question: 'Which is the factored form of x² - 14x + 49?',
        options: ['(x - 7)(x - 7)', '(x + 7)(x + 7)', '(x - 14)(x - 49)', '(x - 1)(x - 49)'],
        correctAnswer: 0,
        explanation: 'This is a perfect square trinomial: x² - 14x + 49 = (x - 7)².'
      },
      {
        question: 'Factor: x² + 3x - 10',
        options: ['(x + 5)(x - 2)', '(x - 5)(x + 2)', '(x + 10)(x - 1)', '(x - 10)(x + 1)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to -10 and add to 3: 5 and -2. So x² + 3x - 10 = (x + 5)(x - 2).'
      },
      {
        question: 'Rewrite x² + 10x + 7 by completing the square.',
        options: ['(x + 5)² - 18', '(x + 10)² + 7', '(x + 5)² + 7', '(x + 5)² - 25'],
        correctAnswer: 0,
        explanation: 'Half of 10 is 5, squared is 25. So x² + 10x + 25 - 25 + 7 = (x + 5)² - 18.'
      },
      {
        question: 'Factor: 4x² + 12x + 9',
        options: ['(2x + 3)(2x + 3)', '(4x + 9)(x + 1)', '(2x + 9)(2x + 1)', '(x + 3)(4x + 3)'],
        correctAnswer: 0,
        explanation: 'This is a perfect square trinomial: 4x² + 12x + 9 = (2x + 3)².'
      },
      {
        question: 'Factor: x² - x - 12',
        options: ['(x - 4)(x + 3)', '(x + 4)(x - 3)', '(x - 6)(x + 2)', '(x + 6)(x - 2)'],
        correctAnswer: 0,
        explanation: 'Find two numbers that multiply to -12 and add to -1: -4 and 3. So x² - x - 12 = (x - 4)(x + 3).'
      },
      {
        question: 'Factor: 16x² - 25',
        options: ['(4x + 5)(4x - 5)', '(16x - 25)(x + 1)', '(8x + 5)(2x - 5)', '(4x - 5)(4x - 5)'],
        correctAnswer: 0,
        explanation: 'This is a difference of squares: (4x)² - 5² = (4x + 5)(4x - 5).'
      },
      {
        question: 'Factor completely: 3x³ - 12x',
        options: ['3x(x² - 4)', '3x(x + 2)(x - 2)', 'x(3x² - 12)', '3(x³ - 4x)'],
        correctAnswer: 1,
        explanation: 'Factor out GCF of 3x: 3x(x² - 4). Then use difference of squares: 3x(x + 2)(x - 2).'
      },
      {
        question: 'Rewrite 2x² + 12x + 5 by completing the square.',
        options: ['2(x + 3)² - 13', '2(x + 6)² + 5', '(2x + 6)² + 5', '2(x + 3)² + 5'],
        correctAnswer: 0,
        explanation: 'Factor out 2: 2(x² + 6x) + 5. Complete the square: 2(x² + 6x + 9 - 9) + 5 = 2(x + 3)² - 18 + 5 = 2(x + 3)² - 13.'
      }
    ]
  },
  {
    id: 'polynomial-operations',
    unit: 2,
    unitName: 'Algebra — Expressions & Equations',
    title: 'Arithmetic with Polynomials and Rational Expressions',
    standards: [
      'CCSS.MATH.CONTENT.HSA.APR.A.1',
      'CCSS.MATH.CONTENT.HSA.APR.B.3',
      'NY-9.A.APR.1',
      'NY-9.A.APR.3',
      'OH.MA.A1.APR.1',
      'NCTM: Algebra - Polynomials'
    ],
    vocabulary: [
      { term: 'Polynomial', definition: 'An expression with one or more terms involving variables with whole number exponents' },
      { term: 'Degree', definition: 'The highest exponent of the variable in a polynomial' },
      { term: 'Zero (Root)', definition: 'A value of x that makes the polynomial equal to zero; where the graph crosses the x-axis' },
      { term: 'Factor Theorem', definition: 'If (x - a) is a factor of polynomial P(x), then P(a) = 0' }
    ],
    conceptExplanation: `Polynomials are fundamental algebraic expressions that we can add, subtract, multiply, and factor.

**Key Concepts:**

1. **Adding and Subtracting Polynomials:**
   - Combine like terms (terms with the same variable and exponent)
   - Example: (3x² + 5x - 2) + (x² - 3x + 7) = 4x² + 2x + 5

2. **Multiplying Polynomials:**
   - Use the distributive property (FOIL for binomials)
   - Multiply each term in the first polynomial by each term in the second
   - Example: (x + 3)(x + 2) = x² + 2x + 3x + 6 = x² + 5x + 6

3. **Zeros and Factors:**
   - If (x - a) is a factor, then x = a is a zero
   - A polynomial of degree n has at most n zeros
   - Factor Theorem: P(a) = 0 if and only if (x - a) is a factor of P(x)

4. **Remainder Theorem:**
   - When dividing P(x) by (x - a), the remainder is P(a)`,
    examples: [
      {
        problem: 'Add: (2x³ - 5x² + 3x - 1) + (x³ + 2x² - x + 4)',
        steps: [
          'Step 1: Align like terms vertically or group them',
          'Step 2: Combine x³ terms: 2x³ + x³ = 3x³',
          'Step 3: Combine x² terms: -5x² + 2x² = -3x²',
          'Step 4: Combine x terms: 3x + (-x) = 2x',
          'Step 5: Combine constants: -1 + 4 = 3',
          'Final Answer: 3x³ - 3x² + 2x + 3'
        ]
      },
      {
        problem: 'Multiply: (2x - 3)(x² + 4x - 5)',
        steps: [
          'Step 1: Multiply 2x by each term in the second polynomial: 2x(x²) + 2x(4x) + 2x(-5) = 2x³ + 8x² - 10x',
          'Step 2: Multiply -3 by each term in the second polynomial: -3(x²) + (-3)(4x) + (-3)(-5) = -3x² - 12x + 15',
          'Step 3: Combine all terms: 2x³ + 8x² - 10x - 3x² - 12x + 15',
          'Step 4: Combine like terms: 2x³ + (8x² - 3x²) + (-10x - 12x) + 15',
          'Final Answer: 2x³ + 5x² - 22x + 15'
        ]
      },
      {
        problem: 'If P(x) = x³ - 6x² + 11x - 6, show that (x - 1) is a factor',
        steps: [
          'Step 1: Use the Factor Theorem. If (x - 1) is a factor, then P(1) = 0',
          'Step 2: Evaluate P(1): P(1) = (1)³ - 6(1)² + 11(1) - 6',
          'Step 3: Simplify: P(1) = 1 - 6 + 11 - 6 = 0',
          'Step 4: Since P(1) = 0, by the Factor Theorem, (x - 1) is indeed a factor',
          'Final Answer: Yes, (x - 1) is a factor because P(1) = 0'
        ]
      }
    ],
    teacherTip: 'When multiplying polynomials, stay organized! Write each step clearly and combine like terms at the end. For zeros and factors, remember: if x = a makes the polynomial zero, then (x - a) is a factor.',
    practiceQuestions: [
      {
        question: 'What is the degree of the polynomial 5x⁴ - 3x² + 7x - 2?',
        options: ['2', '3', '4', '5'],
        correctAnswer: 2,
        explanation: 'The degree is the highest exponent of the variable. The highest exponent is 4.'
      },
      {
        question: 'Add: (3x² + 5x - 2) + (2x² - 3x + 7)',
        options: ['5x² + 2x + 5', '5x² + 8x + 5', '5x² + 2x - 9', 'x² + 2x + 5'],
        correctAnswer: 0,
        explanation: 'Combine like terms: (3x² + 2x²) + (5x - 3x) + (-2 + 7) = 5x² + 2x + 5.'
      },
      {
        question: 'Subtract: (4x³ - 2x + 5) - (x³ + 3x - 1)',
        options: ['3x³ - 5x + 6', '3x³ + x + 4', '5x³ - 5x + 6', '3x³ - 5x + 4'],
        correctAnswer: 0,
        explanation: 'Distribute the negative: 4x³ - 2x + 5 - x³ - 3x + 1 = (4x³ - x³) + (-2x - 3x) + (5 + 1) = 3x³ - 5x + 6.'
      },
      {
        question: 'Multiply: (x + 4)(x + 3)',
        options: ['x² + 7x + 12', 'x² + 12x + 7', 'x² + 7x + 7', 'x² + 3x + 12'],
        correctAnswer: 0,
        explanation: 'Use FOIL: x·x + x·3 + 4·x + 4·3 = x² + 3x + 4x + 12 = x² + 7x + 12.'
      },
      {
        question: 'Multiply: (2x - 5)(x + 3)',
        options: ['2x² + x - 15', '2x² - x - 15', '2x² + 11x - 15', '2x² - 11x - 15'],
        correctAnswer: 0,
        explanation: 'Use FOIL: 2x·x + 2x·3 + (-5)·x + (-5)·3 = 2x² + 6x - 5x - 15 = 2x² + x - 15.'
      },
      {
        question: 'If P(x) = x² - 5x + 6, what is P(2)?',
        options: ['0', '2', '4', '6'],
        correctAnswer: 0,
        explanation: 'P(2) = (2)² - 5(2) + 6 = 4 - 10 + 6 = 0.'
      },
      {
        question: 'If P(3) = 0 for polynomial P(x), which is a factor of P(x)?',
        options: ['(x + 3)', '(x - 3)', '(3x)', '(x - 0)'],
        correctAnswer: 1,
        explanation: 'By the Factor Theorem, if P(3) = 0, then (x - 3) is a factor of P(x).'
      },
      {
        question: 'Multiply: 3x(2x² - 5x + 1)',
        options: ['6x³ - 15x² + 3x', '6x³ - 5x + 3x', '5x³ - 15x² + 3x', '6x² - 15x + 3'],
        correctAnswer: 0,
        explanation: 'Distribute 3x to each term: 3x·2x² + 3x·(-5x) + 3x·1 = 6x³ - 15x² + 3x.'
      },
      {
        question: 'Add: (x³ + 2x² - x) + (3x³ - x² + 4)',
        options: ['4x³ + x² - x + 4', '4x³ + 3x² - x + 4', '4x³ + x² + 3x + 4', '3x³ + x² - x + 4'],
        correctAnswer: 0,
        explanation: 'Combine like terms: (x³ + 3x³) + (2x² - x²) + (-x) + 4 = 4x³ + x² - x + 4.'
      },
      {
        question: 'Multiply: (x - 2)(x + 2)',
        options: ['x² - 4', 'x² + 4', 'x² - 2x + 4', 'x² + 2x - 4'],
        correctAnswer: 0,
        explanation: 'This is a difference of squares: (x - 2)(x + 2) = x² - 4.'
      },
      {
        question: 'If P(x) = x² - 7x + 12, which value is a zero of P(x)?',
        options: ['x = 2', 'x = 3', 'x = 4', 'All of the above'],
        correctAnswer: 2,
        explanation: 'Factor: P(x) = (x - 3)(x - 4). The zeros are x = 3 and x = 4. So x = 4 is a zero.'
      },
      {
        question: 'Subtract: (5x² - 3x + 1) - (2x² + x - 4)',
        options: ['-3x² + 4x + 5', '-3x² - 2x + 5', '0 - 3x² + 4x + 5', '-3x² + 4x + 5'],
        correctAnswer: 0,
        explanation: 'Distribute the negative: 5x² - 3x + 1 - 2x² - x + 4 = -3x² + 4x + 5.'
      },
      {
        question: 'Multiply: (x + 5)²',
        options: ['x² + 25', 'x² + 10x + 25', 'x² + 5x + 25', 'x² + 10x + 10'],
        correctAnswer: 1,
        explanation: '(x + 5)² = (x + 5)(x + 5) = x² + 5x + 5x + 25 = x² + 10x + 25.'
      },
      {
        question: 'If (x + 2) is a factor of P(x), what is P(-2)?',
        options: ['-2', '0', '2', 'Cannot determine'],
        correctAnswer: 1,
        explanation: 'By the Factor Theorem, if (x + 2) is a factor, then P(-2) = 0.'
      },
      {
        question: 'Multiply: (3x + 1)(2x - 5)',
        options: ['6x² - 13x - 5', '6x² - 15x - 5', '6x² + 13x - 5', '6x² - 13x + 5'],
        correctAnswer: 0,
        explanation: 'Use FOIL: 3x·2x + 3x·(-5) + 1·2x + 1·(-5) = 6x² - 15x + 2x - 5 = 6x² - 13x - 5.'
      },
      {
        question: 'Add: (2x⁴ - x² + 3) + (x⁴ + 5x² - 1)',
        options: ['3x⁴ + 4x² + 2', '3x⁴ - 6x² + 2', '2x⁴ + 4x² + 2', '3x⁴ + 4x² + 4'],
        correctAnswer: 0,
        explanation: 'Combine like terms: (2x⁴ + x⁴) + (-x² + 5x²) + (3 - 1) = 3x⁴ + 4x² + 2.'
      },
      {
        question: 'What is the degree of the polynomial 7 - 2x + 4x³?',
        options: ['0', '1', '2', '3'],
        correctAnswer: 3,
        explanation: 'The degree is the highest exponent. The highest exponent is 3 (from 4x³).'
      },
      {
        question: 'Multiply: -2x²(3x² - 4x + 1)',
        options: ['-6x⁴ + 8x³ - 2x²', '-6x⁴ - 8x³ - 2x²', '-6x⁴ + 8x³ + 2x²', '-5x⁴ + 8x³ - 2x²'],
        correctAnswer: 0,
        explanation: 'Distribute -2x²: -2x²·3x² + (-2x²)·(-4x) + (-2x²)·1 = -6x⁴ + 8x³ - 2x².'
      },
      {
        question: 'If P(x) = x³ - 4x² + x + 6 and P(2) = 0, which is a factor?',
        options: ['(x + 2)', '(x - 2)', '(2x)', '(x - 6)'],
        correctAnswer: 1,
        explanation: 'By the Factor Theorem, if P(2) = 0, then (x - 2) is a factor.'
      },
      {
        question: 'Multiply: (x - 3)(x² + 3x + 9)',
        options: ['x³ - 27', 'x³ + 27', 'x³ - 9x - 27', 'x³ + 6x² - 27'],
        correctAnswer: 0,
        explanation: 'This is the pattern (a - b)(a² + ab + b²) = a³ - b³. So (x - 3)(x² + 3x + 9) = x³ - 27.'
      },
      {
        question: 'Subtract: (6x³ - 2x² + x - 5) - (6x³ + x² - 3x + 2)',
        options: ['-3x² + 4x - 7', '-3x² - 2x - 7', '0 - 3x² + 4x - 7', '-3x² + 4x - 3'],
        correctAnswer: 0,
        explanation: 'Distribute negative: 6x³ - 2x² + x - 5 - 6x³ - x² + 3x - 2 = -3x² + 4x - 7.'
      },
      {
        question: 'Multiply: (2x + 3)²',
        options: ['4x² + 9', '4x² + 6x + 9', '4x² + 12x + 9', '2x² + 12x + 9'],
        correctAnswer: 2,
        explanation: '(2x + 3)² = (2x)² + 2(2x)(3) + 3² = 4x² + 12x + 9.'
      },
      {
        question: 'If P(x) = x² + kx + 12 and (x - 3) is a factor, what is k?',
        options: ['-7', '-4', '4', '7'],
        correctAnswer: 0,
        explanation: 'If (x - 3) is a factor, then P(3) = 0. So 9 + 3k + 12 = 0, which gives 3k = -21, so k = -7.'
      },
      {
        question: 'Add: (x² - 3x + 2) + (x² + 3x - 2)',
        options: ['2x²', '2x² + 0x + 0', '2x² - 6x + 4', 'x² + 0'],
        correctAnswer: 0,
        explanation: 'Combine like terms: (x² + x²) + (-3x + 3x) + (2 - 2) = 2x² + 0 + 0 = 2x².'
      },
      {
        question: 'Multiply: (x + 1)(x - 1)(x + 2)',
        options: ['x³ + 2x² - x - 2', 'x³ - 2x² + x - 2', 'x³ + 2x² + x - 2', 'x³ + x² - x - 2'],
        correctAnswer: 0,
        explanation: 'First multiply (x + 1)(x - 1) = x² - 1. Then (x² - 1)(x + 2) = x³ + 2x² - x - 2.'
      },
      {
        question: 'How many zeros does a polynomial of degree 5 have at most?',
        options: ['3', '4', '5', 'Infinite'],
        correctAnswer: 2,
        explanation: 'A polynomial of degree n has at most n zeros. So a degree 5 polynomial has at most 5 zeros.'
      },
      {
        question: 'Multiply: (x - 4)(x + 4)',
        options: ['x² - 16', 'x² + 16', 'x² - 8x + 16', 'x² + 8x - 16'],
        correctAnswer: 0,
        explanation: 'This is a difference of squares: (x - 4)(x + 4) = x² - 16.'
      },
      {
        question: 'If P(x) = 2x³ - 5x² + x - 3, what is the degree?',
        options: ['2', '3', '5', '-3'],
        correctAnswer: 1,
        explanation: 'The degree is the highest exponent of the variable, which is 3.'
      },
      {
        question: 'Subtract: (4x² + 7x - 1) - (4x² - 2x + 5)',
        options: ['9x - 6', '9x + 4', '5x - 6', '2x - 6'],
        correctAnswer: 0,
        explanation: 'Distribute negative: 4x² + 7x - 1 - 4x² + 2x - 5 = (7x + 2x) + (-1 - 5) = 9x - 6.'
      },
      {
        question: 'Multiply: (3x - 2)(3x + 2)',
        options: ['9x² - 4', '9x² + 4', '9x² - 12x + 4', '9x² + 12x - 4'],
        correctAnswer: 0,
        explanation: 'This is a difference of squares: (3x - 2)(3x + 2) = (3x)² - 2² = 9x² - 4.'
      }
    ]
  },
  {
    id: 'creating-equations',
    unit: 2,
    unitName: 'Algebra — Expressions & Equations',
    title: 'Creating Equations and Inequalities',
    standards: [
      'CCSS.MATH.CONTENT.HSA.CED.A.1',
      'CCSS.MATH.CONTENT.HSA.CED.A.2',
      'CCSS.MATH.CONTENT.HSA.CED.A.3',
      'CCSS.MATH.CONTENT.HSA.CED.A.4',
      'NY-9.A.CED.1',
      'NY-9.A.CED.2',
      'OH.MA.A1.CED.1',
      'NCTM: Algebra - Equations'
    ],
    vocabulary: [
      { term: 'Variable', definition: 'A letter or symbol that represents an unknown quantity' },
      { term: 'Constraint', definition: 'A limitation or restriction on the values a variable can take' },
      { term: 'Inequality', definition: 'A mathematical statement showing that two expressions are not equal (using <, >, ≤, or ≥)' },
      { term: 'System of Equations', definition: 'Two or more equations with the same variables that are solved together' }
    ],
    conceptExplanation: `Creating equations and inequalities from real-world situations is a crucial skill in algebra.

**Key Concepts:**

1. **Translating Words to Math:**
   - "Sum" → addition (+)
   - "Difference" → subtraction (-)
   - "Product" → multiplication (×)
   - "Quotient" → division (÷)
   - "Is," "equals," "results in" → equals sign (=)
   - "At least" → ≥
   - "At most" → ≤
   - "More than" → >
   - "Less than" → <

2. **One-Variable Equations:**
   - Define the variable clearly
   - Write an equation based on the problem
   - Example: "Five more than twice a number is 17" → 2x + 5 = 17

3. **Two-Variable Equations:**
   - Used when two quantities are related
   - Example: "The cost is $5 per item plus a $20 fee" → C = 5n + 20

4. **Inequalities:**
   - Represent constraints or limitations
   - Example: "You must score at least 80 points" → s ≥ 80

5. **Systems of Equations:**
   - Multiple equations with multiple variables
   - Represent situations with multiple constraints`,
    examples: [
      {
        problem: 'A rental car costs $40 per day plus $0.25 per mile. Write an equation for the total cost C for d days and m miles.',
        steps: [
          'Step 1: Identify the variables: C = total cost, d = days, m = miles',
          'Step 2: Identify the fixed cost: $40 per day means 40d',
          'Step 3: Identify the variable cost: $0.25 per mile means 0.25m',
          'Step 4: Add the costs together',
          'Final Answer: C = 40d + 0.25m'
        ]
      },
      {
        problem: 'The sum of two numbers is 50, and one number is 8 more than the other. Write a system of equations.',
        steps: [
          'Step 1: Define variables. Let x = first number, y = second number',
          'Step 2: "The sum is 50" translates to: x + y = 50',
          'Step 3: "One number is 8 more than the other" translates to: y = x + 8 (or x = y + 8)',
          'Step 4: Write the system',
          'Final Answer: x + y = 50 and y = x + 8'
        ]
      },
      {
        problem: 'A student needs at least 90 points to get an A. She has scored 85, 92, and 88 on three tests. Write an inequality for the score s she needs on the fourth test.',
        steps: [
          'Step 1: The average of four tests must be at least 90',
          'Step 2: Write the average formula: (85 + 92 + 88 + s) / 4 ≥ 90',
          'Step 3: Simplify the numerator: (265 + s) / 4 ≥ 90',
          'Step 4: This is the inequality',
          'Final Answer: (265 + s) / 4 ≥ 90 or 265 + s ≥ 360'
        ]
      }
    ],
    teacherTip: 'Always start by clearly defining your variables! Write down what each letter represents. Then, translate the problem sentence by sentence into mathematical expressions.',
    practiceQuestions: [
      {
        question: 'Translate to an equation: "Seven less than three times a number is 20."',
        options: ['3x - 7 = 20', '7 - 3x = 20', '3(x - 7) = 20', 'x/3 - 7 = 20'],
        correctAnswer: 0,
        explanation: '"Three times a number" is 3x. "Seven less than" means subtract 7. So: 3x - 7 = 20.'
      },
      {
        question: 'A gym charges $30 per month plus a $50 enrollment fee. Write an equation for total cost C after m months.',
        options: ['C = 30m + 50', 'C = 50m + 30', 'C = 30 + 50m', 'C = 80m'],
        correctAnswer: 0,
        explanation: '$30 per month is 30m. Add the one-time $50 fee: C = 30m + 50.'
      },
      {
        question: 'Translate to an inequality: "You must be at least 16 years old to drive."',
        options: ['a > 16', 'a < 16', 'a ≥ 16', 'a ≤ 16'],
        correctAnswer: 2,
        explanation: '"At least 16" means 16 or more, which is a ≥ 16.'
      },
      {
        question: 'The perimeter of a rectangle is 40 cm. If the length is L and width is W, write an equation.',
        options: ['L + W = 40', '2L + 2W = 40', 'LW = 40', 'L + W = 20'],
        correctAnswer: 1,
        explanation: 'Perimeter of a rectangle is 2L + 2W. So 2L + 2W = 40.'
      },
      {
        question: 'Translate: "The product of a number and 5 is greater than 30."',
        options: ['5x > 30', '5x < 30', 'x + 5 > 30', '5 + x > 30'],
        correctAnswer: 0,
        explanation: '"Product of a number and 5" is 5x. "Greater than 30" is > 30. So: 5x > 30.'
      },
      {
        question: 'A store sells shirts for $15 each. Write an equation for revenue R from selling s shirts.',
        options: ['R = 15s', 'R = s + 15', 'R = 15/s', 's = 15R'],
        correctAnswer: 0,
        explanation: 'Revenue is price times quantity. $15 per shirt times s shirts: R = 15s.'
      },
      {
        question: 'Translate: "A number is at most 25."',
        options: ['x < 25', 'x > 25', 'x ≤ 25', 'x ≥ 25'],
        correctAnswer: 2,
        explanation: '"At most 25" means 25 or less, which is x ≤ 25.'
      },
      {
        question: 'The length of a rectangle is 3 more than twice the width. If width is w, write an equation for length L.',
        options: ['L = 2w + 3', 'L = 3w + 2', 'L = 2(w + 3)', 'L = w + 3'],
        correctAnswer: 0,
        explanation: 'Twice the width is 2w. "3 more than" means add 3. So L = 2w + 3.'
      },
      {
        question: 'Translate: "A number decreased by 8 is less than 20."',
        options: ['x - 8 < 20', '8 - x < 20', 'x - 8 > 20', 'x + 8 < 20'],
        correctAnswer: 0,
        explanation: '"A number decreased by 8" is x - 8. "Is less than 20" is < 20. So: x - 8 < 20.'
      },
      {
        question: 'You have $100 to spend. Items cost $12 each. Write an inequality for the number n you can buy.',
        options: ['12n ≤ 100', '12n ≥ 100', '12n < 100', 'n ≤ 12'],
        correctAnswer: 0,
        explanation: 'Total cost is 12n. You can spend at most $100, so 12n ≤ 100.'
      },
      {
        question: 'The difference between two numbers is 15. If the larger is x, write an equation for the smaller y.',
        options: ['y = x - 15', 'y = 15 - x', 'x = y - 15', 'x - y = 15'],
        correctAnswer: 0,
        explanation: 'If x is larger and the difference is 15, then x - y = 15, so y = x - 15.'
      },
      {
        question: 'Translate: "Four times a number plus 7 equals 31."',
        options: ['4x + 7 = 31', '4(x + 7) = 31', '7x + 4 = 31', 'x + 4 + 7 = 31'],
        correctAnswer: 0,
        explanation: '"Four times a number" is 4x. "Plus 7" means add 7. "Equals 31" is = 31. So: 4x + 7 = 31.'
      },
      {
        question: 'A taxi charges $3 plus $2 per mile. Write an equation for cost C for m miles.',
        options: ['C = 2m + 3', 'C = 3m + 2', 'C = 5m', 'C = 2 + 3m'],
        correctAnswer: 0,
        explanation: '$3 is the base fare. $2 per mile is 2m. Total: C = 2m + 3 or C = 3 + 2m.'
      },
      {
        question: 'The area of a triangle is 24 cm². If base is b and height is h, write an equation.',
        options: ['bh = 24', '(1/2)bh = 24', 'b + h = 24', '2bh = 24'],
        correctAnswer: 1,
        explanation: 'Area of a triangle is (1/2) × base × height. So (1/2)bh = 24.'
      },
      {
        question: 'Translate: "A number decreased by 8 is less than 20."',
        options: ['x - 8 < 20', '8 - x < 20', 'x - 8 > 20', 'x + 8 < 20'],
        correctAnswer: 0,
        explanation: '"A number decreased by 8" is x - 8. "Is less than 20" is < 20. So: x - 8 < 20.'
      },
      {
        question: 'You need to save at least $500. You have $200 and save $25 per week. Write an inequality for weeks w.',
        options: ['200 + 25w ≥ 500', '200 + 25w ≤ 500', '25w ≥ 500', '200 + 25w = 500'],
        correctAnswer: 0,
        explanation: 'You start with $200 and add $25 per week (25w). You need at least $500: 200 + 25w ≥ 500.'
      },
      {
        question: 'The sum of two numbers is 60 and their difference is 10. If the larger is x and smaller is y, write a system.',
        options: ['x + y = 60 and x - y = 10', 'x + y = 10 and x - y = 60', 'x + y = 60 and y - x = 10', 'xy = 60 and x - y = 10'],
        correctAnswer: 0,
        explanation: '"Sum is 60" gives x + y = 60. "Difference is 10" (larger minus smaller) gives x - y = 10.'
      },
      {
        question: 'Translate: "One-third of a number increased by 5 is 14."',
        options: ['x/3 + 5 = 14', '5 + 3x = 14', '(x + 5)/3 = 14', 'x/3 - 5 = 14'],
        correctAnswer: 0,
        explanation: '"One-third of a number" is x/3. "Increased by 5" means add 5. "Is 14" is = 14. So: x/3 + 5 = 14.'
      },
      {
        question: 'A store sells apples for $2 per pound and oranges for $3 per pound. Write an equation for total cost C for a pounds of apples and o pounds of oranges.',
        options: ['C = 2a + 3o', 'C = 3a + 2o', 'C = 5(a + o)', 'C = 2 + 3 + a + o'],
        correctAnswer: 0,
        explanation: 'Apples cost $2 per pound (2a). Oranges cost $3 per pound (3o). Total: C = 2a + 3o.'
      },
      {
        question: 'You want to score an average of at least 85 on four tests. You scored 80, 88, and 82 on the first three. Write an inequality for the fourth score s.',
        options: ['(80 + 88 + 82 + s)/4 ≥ 85', '(80 + 88 + 82 + s)/4 ≤ 85', '80 + 88 + 82 + s ≥ 85', 's ≥ 85'],
        correctAnswer: 0,
        explanation: 'Average of four tests is (sum of four scores)/4. You want this at least 85: (80 + 88 + 82 + s)/4 ≥ 85.'
      },
      {
        question: 'The perimeter of a square is P and each side is s. Write an equation.',
        options: ['P = 4s', 'P = s²', 'P = 2s', 's = 4P'],
        correctAnswer: 0,
        explanation: 'A square has 4 equal sides. Perimeter is 4 times the side length: P = 4s.'
      }
    ]
  },
  {
    id: 'solving-equations-inequalities',
    title: 'Reasoning with Equations and Inequalities',
    standards: [
      'CCSS.MATH.CONTENT.HSA.REI.A.1',
      'CCSS.MATH.CONTENT.HSA.REI.B.3',
      'CCSS.MATH.CONTENT.HSA.REI.C.5',
      'CCSS.MATH.CONTENT.HSA.REI.C.6',
      'CCSS.MATH.CONTENT.HSA.REI.D.10',
      'CCSS.MATH.CONTENT.HSA.REI.D.11',
      'NY-9.A.REI.1',
      'NY-9.A.REI.3',
      'NY-9.A.REI.5',
      'NY-9.A.REI.6',
      'OH.MA.A1.REI.1',
      'OH.MA.A1.REI.3',
      'NCTM: Algebra - Equations & Inequalities'
    ],
    vocabulary: [
      { term: 'Solution', definition: 'A value that makes an equation or inequality true' },
      { term: 'System of Equations', definition: 'Two or more equations with the same variables solved together' },
      { term: 'Substitution Method', definition: 'Solving a system by replacing one variable with an expression from another equation' },
      { term: 'Elimination Method', definition: 'Solving a system by adding or subtracting equations to eliminate a variable' }
    ],
    conceptExplanation: `Solving equations and inequalities requires understanding properties of equality and systematic methods.

**Key Concepts:**

1. **Solving Linear Equations:**
   - Use inverse operations to isolate the variable
   - Whatever you do to one side, do to the other
   - Example: 3x + 5 = 14 → 3x = 9 → x = 3

2. **Solving Linear Inequalities:**
   - Same process as equations
   - IMPORTANT: Flip the inequality sign when multiplying or dividing by a negative number
   - Example: -2x < 6 → x > -3 (sign flipped!)

3. **Systems of Linear Equations - Substitution:**
   - Solve one equation for one variable
   - Substitute into the other equation
   - Solve for the remaining variable
   - Back-substitute to find the other variable

4. **Systems of Linear Equations - Elimination:**
   - Multiply equations to make coefficients of one variable opposites
   - Add equations to eliminate that variable
   - Solve for the remaining variable
   - Back-substitute to find the other variable

5. **Solving Quadratic Equations:**
   - Factoring: Set equal to zero, factor, use Zero Product Property
   - Square Root Method: Isolate x², take square root of both sides
   - Completing the Square: Create perfect square trinomial
   - Quadratic Formula: x = [-b ± √(b² - 4ac)] / (2a)`,
    examples: [
      {
        problem: 'Solve the system using substitution: y = 2x + 1 and x + y = 7',
        steps: [
          'Step 1: The first equation is already solved for y: y = 2x + 1',
          'Step 2: Substitute (2x + 1) for y in the second equation: x + (2x + 1) = 7',
          'Step 3: Simplify and solve for x: 3x + 1 = 7 → 3x = 6 → x = 2',
          'Step 4: Substitute x = 2 back into y = 2x + 1: y = 2(2) + 1 = 5',
          'Final Answer: x = 2, y = 5 or (2, 5)'
        ]
      },
      {
        problem: 'Solve the system using elimination: 2x + 3y = 12 and 3x - 3y = 3',
        steps: [
          'Step 1: Notice that the y-coefficients are opposites (3 and -3)',
          'Step 2: Add the equations to eliminate y: (2x + 3y) + (3x - 3y) = 12 + 3',
          'Step 3: Simplify: 5x = 15 → x = 3',
          'Step 4: Substitute x = 3 into first equation: 2(3) + 3y = 12 → 6 + 3y = 12 → 3y = 6 → y = 2',
          'Final Answer: x = 3, y = 2 or (3, 2)'
        ]
      },
      {
        problem: 'Solve by factoring: x² - 5x + 6 = 0',
        steps: [
          'Step 1: Factor the quadratic: (x - 2)(x - 3) = 0',
          'Step 2: Use the Zero Product Property: if ab = 0, then a = 0 or b = 0',
          'Step 3: Set each factor equal to zero: x - 2 = 0 or x - 3 = 0',
          'Step 4: Solve each equation: x = 2 or x = 3',
          'Final Answer: x = 2 or x = 3'
        ]
      }
    ],
    teacherTip: 'For systems of equations, choose the method that looks easiest! If one equation is already solved for a variable, use substitution. If coefficients are opposites or can easily be made opposites, use elimination.',
    practiceQuestions: [
      {
        question: 'Solve: 2x + 5 = 13',
        options: ['x = 4', 'x = 9', 'x = 8', 'x = 6.5'],
        correctAnswer: 0,
        explanation: 'Subtract 5 from both sides: 2x = 8. Divide by 2: x = 4.'
      },
      {
        question: 'Solve: 3x - 7 = 11',
        options: ['x = 6', 'x = 4', 'x = 18', 'x = 4/3'],
        correctAnswer: 0,
        explanation: 'Add 7 to both sides: 3x = 18. Divide by 3: x = 6.'
      },
      {
        question: 'Solve: -4x = 20',
        options: ['x = -5', 'x = 5', 'x = -80', 'x = 80'],
        correctAnswer: 0,
        explanation: 'Divide both sides by -4: x = 20 / (-4) = -5.'
      },
      {
        question: 'Solve: x/3 = 5',
        options: ['x = 15', 'x = 5/3', 'x = 8', 'x = 2'],
        correctAnswer: 0,
        explanation: 'Multiply both sides by 3: x = 5 × 3 = 15.'
      },
      {
        question: 'Solve: 2(x + 3) = 14',
        options: ['x = 4', 'x = 7', 'x = 10', 'x = 5.5'],
        correctAnswer: 0,
        explanation: 'Distribute: 2x + 6 = 14. Subtract 6: 2x = 8. Divide by 2: x = 4.'
      },
      {
        question: 'Solve: -2x > 10',
        options: ['x < -5', 'x > -5', 'x < 5', 'x > 5'],
        correctAnswer: 0,
        explanation: 'Divide by -2 and flip the inequality sign: x < -5.'
      },
      {
        question: 'Solve: 3x + 2 ≤ 11',
        options: ['x ≤ 3', 'x ≥ 3', 'x ≤ 13/3', 'x < 3'],
        correctAnswer: 0,
        explanation: 'Subtract 2: 3x ≤ 9. Divide by 3: x ≤ 3.'
      },
      {
        question: 'Solve using substitution: y = x + 2 and x + y = 8',
        options: ['x = 3, y = 5', 'x = 5, y = 3', 'x = 4, y = 4', 'x = 2, y = 6'],
        correctAnswer: 0,
        explanation: 'Substitute y = x + 2 into x + y = 8: x + (x + 2) = 8 → 2x + 2 = 8 → 2x = 6 → x = 3. Then y = 3 + 2 = 5.'
      },
      {
        question: 'Solve using elimination: x + y = 10 and x - y = 4',
        options: ['x = 7, y = 3', 'x = 3, y = 7', 'x = 5, y = 5', 'x = 6, y = 4'],
        correctAnswer: 0,
        explanation: 'Add the equations: (x + y) + (x - y) = 10 + 4 → 2x = 14 → x = 7. Substitute: 7 + y = 10 → y = 3.'
      },
      {
        question: 'Solve by factoring: x² - 7x + 12 = 0',
        options: ['x = 3 or x = 4', 'x = -3 or x = -4', 'x = 2 or x = 6', 'x = 1 or x = 12'],
        correctAnswer: 0,
        explanation: 'Factor: (x - 3)(x - 4) = 0. So x - 3 = 0 or x - 4 = 0, giving x = 3 or x = 4.'
      },
      {
        question: 'Solve: 5x - 3 = 2x + 9',
        options: ['x = 4', 'x = 2', 'x = 6', 'x = 3'],
        correctAnswer: 0,
        explanation: 'Subtract 2x from both sides: 3x - 3 = 9. Add 3: 3x = 12. Divide by 3: x = 4.'
      },
      {
        question: 'Solve: 4(x - 2) = 3(x + 1)',
        options: ['x = 11', 'x = 5', 'x = 7', 'x = 9'],
        correctAnswer: 0,
        explanation: 'Distribute: 4x - 8 = 3x + 3. Subtract 3x: x - 8 = 3. Add 8: x = 11.'
      },
      {
        question: 'Solve: x² = 25',
        options: ['x = 5 or x = -5', 'x = 5', 'x = -5', 'x = 12.5'],
        correctAnswer: 0,
        explanation: 'Take the square root of both sides: x = ±5, so x = 5 or x = -5.'
      },
      {
        question: 'Solve: (x - 3)² = 16',
        options: ['x = 7 or x = -1', 'x = 19 or x = -13', 'x = 4 or x = -4', 'x = 7'],
        correctAnswer: 0,
        explanation: 'Take square root: x - 3 = ±4. So x - 3 = 4 or x - 3 = -4, giving x = 7 or x = -1.'
      },
      {
        question: 'Solve: x/2 + 3 = 7',
        options: ['x = 8', 'x = 4', 'x = 11', 'x = -11'],
        correctAnswer: 0,
        explanation: 'Subtract 3: x/2 = 4. Multiply by 2: x = 8.'
      },
      {
        question: 'Solve: -3x + 5 ≥ 14',
        options: ['x ≤ -3', 'x ≥ -3', 'x ≤ 3', 'x ≥ 3'],
        correctAnswer: 0,
        explanation: 'Subtract 5: -3x ≥ 9. Divide by -3 and flip sign: x ≤ -3.'
      },
      {
        question: 'Solve using substitution: x = 2y and 3x + y = 14',
        options: ['x = 4, y = 2', 'x = 2, y = 4', 'x = 6, y = 3', 'x = 8, y = 4'],
        correctAnswer: 0,
        explanation: 'Substitute x = 2y into 3x + y = 14: 3(2y) + y = 14 → 6y + y = 14 → 7y = 14 → y = 2. Then x = 2(2) = 4.'
      },
      {
        question: 'Solve using elimination: 3x + 2y = 16 and 3x - 2y = 8',
        options: ['x = 4, y = 2', 'x = 2, y = 4', 'x = 3, y = 3.5', 'x = 5, y = 0.5'],
        correctAnswer: 0,
        explanation: 'Add equations: (3x + 2y) + (3x - 2y) = 16 + 8 → 6x = 24 → x = 4. Substitute: 3(4) + 2y = 16 → 2y = 4 → y = 2.'
      },
      {
        question: 'Solve by factoring: x² + 5x + 6 = 0',
        options: ['x = -2 or x = -3', 'x = 2 or x = 3', 'x = -1 or x = -6', 'x = 1 or x = 6'],
        correctAnswer: 0,
        explanation: 'Factor: (x + 2)(x + 3) = 0. So x + 2 = 0 or x + 3 = 0, giving x = -2 or x = -3.'
      },
      {
        question: 'Solve: 7 - 2x = 15',
        options: ['x = -7', 'x = 7', 'x = 17', 'x = -17'],
        correctAnswer: 0,
        explanation: 'Subtract 7: -2x = 8. Divide by -2: x = -4.'
      },
      {
        question: 'Solve: 3(x + 4) - 2x = 18',
        options: ['x = 6', 'x = 2', 'x = 10', 'x = 14'],
        correctAnswer: 0,
        explanation: 'Distribute: 3x + 12 - 2x = 18. Combine: x + 12 = 18. Subtract 12: x = 6.'
      },
      {
        question: 'Solve: x² - 9 = 0',
        options: ['x = 3 or x = -3', 'x = 9 or x = -9', 'x = 3', 'x = -3'],
        correctAnswer: 0,
        explanation: 'Add 9: x² = 9. Take square root: x = ±3, so x = 3 or x = -3.'
      },
      {
        question: 'Solve: 2x + 3 < 11',
        options: ['x < 4', 'x > 4', 'x ≤ 4', 'x < 7'],
        correctAnswer: 0,
        explanation: 'Subtract 3: 2x < 8. Divide by 2: x < 4.'
      },
      {
        question: 'Solve using substitution: y = 3x and 2x + y = 10',
        options: ['x = 2, y = 6', 'x = 6, y = 2', 'x = 3, y = 9', 'x = 1, y = 3'],
        correctAnswer: 0,
        explanation: 'Substitute y = 3x into 2x + y = 10: 2x + 3x = 10 → 5x = 10 → x = 2. Then y = 3(2) = 6.'
      },
      {
        question: 'Solve using elimination: 3x + 2y = 16 and 3x - 2y = 8',
        options: ['x = 4, y = 2', 'x = 2, y = 4', 'x = 3, y = 3.5', 'x = 5, y = 0.5'],
        correctAnswer: 0,
        explanation: 'Add equations: (3x + 2y) + (3x - 2y) = 16 + 8 → 6x = 24 → x = 4. Substitute: 3(4) + 2y = 16 → 2y = 4 → y = 2.'
      },
      {
        question: 'Solve by factoring: x² - x - 12 = 0',
        options: ['x = 4 or x = -3', 'x = -4 or x = 3', 'x = 6 or x = -2', 'x = 12 or x = -1'],
        correctAnswer: 0,
        explanation: 'Factor: (x - 4)(x + 3) = 0. So x - 4 = 0 or x + 3 = 0, giving x = 4 or x = -3.'
      },
      {
        question: 'Solve: 5 - x = 12',
        options: ['x = -7', 'x = 7', 'x = 17', 'x = -17'],
        correctAnswer: 0,
        explanation: 'Subtract 5: -x = 7. Multiply by -1: x = -7.'
      },
      {
        question: 'Solve: x² + 6x + 9 = 0',
        options: ['x = -3', 'x = 3', 'x = -3 or x = 3', 'No solution'],
        correctAnswer: 0,
        explanation: 'Factor: (x + 3)² = 0. So x + 3 = 0, giving x = -3 (double root).'
      },
      {
        question: 'Solve: 4x - 1 ≥ 11',
        options: ['x ≥ 3', 'x ≤ 3', 'x > 3', 'x ≥ 2.5'],
        correctAnswer: 0,
        explanation: 'Add 1: 4x ≥ 12. Divide by 4: x ≥ 3.'
      },
      {
        question: 'Solve: (x + 1)(x - 5) = 0',
        options: ['x = -1 or x = 5', 'x = 1 or x = -5', 'x = -1 or x = -5', 'x = 1 or x = 5'],
        correctAnswer: 0,
        explanation: 'By Zero Product Property: x + 1 = 0 or x - 5 = 0, so x = -1 or x = 5.'
      }
    ]
  }
];